import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LifeBuoy, CheckCircle } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { CounterGuestDetailsBody } from '@/components/counter/CounterGuestDetailsSection'
import type { APIResponse, IssuedRecoveryCoupon, Order, RecoveryCouponConfig } from '@/types'

interface RecoveryCouponModalProps {
  orderId: string
  /** True when the order already has a saved customer (customer_id). A
   *  recovery coupon is bound to that customer, so without one we can't issue. */
  customerCaptured: boolean
  /** The order being checked out. Required to attach a customer inline. */
  existingOrder: Order | null
  /** Guest draft state + setters, shared with the toolbar's Guest popover so
   *  the same capture form can be embedded here without losing typed values. */
  customerName: string
  setCustomerName: (v: string) => void
  customerEmail: string
  setCustomerEmail: (v: string) => void
  customerPhone: string
  setCustomerPhone: (v: string) => void
  guestBirthday: string
  setGuestBirthday: (v: string) => void
  onGuestUpdated: (order: Order) => void
  onClose: () => void
}

/**
 * RecoveryCouponModal — manager picks a severity tier (or types a capped
 * override), selects a reason, and authorises with a 4-digit manager PIN to
 * mint a customer-bound comeback code for the next visit. Adapts the PIN-entry
 * pattern from counter/PinEntryModal. Does not touch the current bill.
 *
 * Rendered through the shared `Dialog` (Radix portal → document.body) so it
 * anchors to the viewport rather than the `backdrop-blur` header wrapper it is
 * mounted under, and never clips: a fixed-height flex column with a sticky
 * header, a scrollable body, and a sticky action footer. When no customer is
 * attached yet, the guest-capture form is embedded inline and the modal
 * auto-advances to the issue form the moment a customer is saved.
 */
export function RecoveryCouponModal({
  orderId,
  customerCaptured,
  existingOrder,
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  customerPhone,
  setCustomerPhone,
  guestBirthday,
  setGuestBirthday,
  onGuestUpdated,
  onClose,
}: RecoveryCouponModalProps) {
  const configQuery = useQuery<APIResponse<RecoveryCouponConfig>>({
    queryKey: ['recovery-coupon-config'],
    queryFn: () => apiClient.getRecoveryCouponConfig(),
    staleTime: 60_000,
  })
  const config = configQuery.data?.data

  const [tierKey, setTierKey] = useState('')
  const [override, setOverride] = useState('')
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState(['', '', '', ''])
  const [error, setError] = useState('')
  const [shake, setShake] = useState(false)
  const [issued, setIssued] = useState<IssuedRecoveryCoupon | null>(null)
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  // Default the tier + reason once config arrives.
  useEffect(() => {
    if (!config) return
    if (!tierKey && config.tiers.length > 0) setTierKey(config.tiers[0].key)
    if (!reason && config.reasons.length > 0) setReason(config.reasons[0])
  }, [config, tierKey, reason])

  const selectedTier = useMemo(
    () => config?.tiers.find((t) => t.key === tierKey) ?? null,
    [config, tierKey],
  )

  const overrideNum = override.trim() === '' ? null : Number(override)
  const overrideInvalid =
    overrideNum !== null &&
    (!Number.isFinite(overrideNum) ||
      overrideNum <= 0 ||
      (config ? overrideNum > config.override_cap : false))

  const effectivePercent = overrideNum !== null && !overrideInvalid
    ? overrideNum
    : selectedTier?.percent ?? 0

  const issueMutation = useMutation({
    mutationFn: async () => {
      const pinStr = pin.join('')
      return apiClient.issueRecoveryCoupon(orderId, {
        pin: pinStr,
        tier: tierKey,
        reason,
        override_value: overrideNum !== null && !overrideInvalid ? overrideNum : undefined,
      })
    },
    onSuccess: (res) => {
      if (res.success && res.data) {
        setIssued(res.data)
      } else {
        triggerShake(res.message || 'Could not issue coupon')
      }
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Invalid PIN. Try again.'
      triggerShake(msg)
    },
  })

  const triggerShake = (msg: string) => {
    setError(msg)
    setShake(true)
    setPin(['', '', '', ''])
    setTimeout(() => setShake(false), 500)
    setTimeout(() => inputRefs[0].current?.focus(), 100)
  }

  const handleDigit = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return
    const next = [...pin]
    next[index] = value
    setPin(next)
    setError('')
    if (value && index < 3) inputRefs[index + 1].current?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const canSubmit =
    customerCaptured &&
    !!tierKey &&
    reason.trim() !== '' &&
    !overrideInvalid &&
    pin.every((d) => d !== '') &&
    !issueMutation.isPending

  const displayName = existingOrder?.customer_name || customerName || ''

  // ── Success screen ──────────────────────────────────────────────────
  if (issued) {
    return (
      <Shell subtitle="Coupon issued" onClose={onClose}>
        <div className="flex-1 overflow-y-auto px-6 py-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
            <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-xl font-bold text-foreground">Recovery coupon issued</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {issued.discount_label} off the next visit
            {issued.customer_name ? ` for ${issued.customer_name}` : ''} · valid until{' '}
            {issued.expires_on}
          </p>
          <div
            className="mx-auto mt-5 inline-block px-5 py-3 font-mono text-2xl font-extrabold tracking-widest text-foreground"
            style={{ border: '3px double currentColor' }}
          >
            {issued.code}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Give this code to the guest — it prints on their receipt too. Only they can redeem it.
          </p>
        </div>
        <Footer>
          <Button className="w-full" onClick={onClose}>
            Done
          </Button>
        </Footer>
      </Shell>
    )
  }

  // ── No customer captured — capture them inline, then auto-advance ───
  if (!customerCaptured) {
    return (
      <Shell subtitle="Add the guest to issue a code" onClose={onClose}>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {existingOrder?.id ? (
            <>
              <p className="mb-3 text-xs leading-snug text-muted-foreground">
                Add the guest below — the coupon is tied to them and opens once saved.
              </p>
              <CounterGuestDetailsBody
                customerName={customerName}
                setCustomerName={setCustomerName}
                customerEmail={customerEmail}
                setCustomerEmail={setCustomerEmail}
                customerPhone={customerPhone}
                setCustomerPhone={setCustomerPhone}
                guestBirthday={guestBirthday}
                setGuestBirthday={setGuestBirthday}
                existingOrder={existingOrder}
                onGuestUpdated={onGuestUpdated}
                showSearchHint={false}
              />
            </>
          ) : (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Open or load the order first, then issue a recovery coupon.
            </div>
          )}
        </div>
      </Shell>
    )
  }

  const disabled = configQuery.isLoading || !config?.enabled

  // ── Issue form ──────────────────────────────────────────────────────
  if (disabled) {
    return (
      <Shell
        subtitle={displayName ? `For ${displayName} · next visit only` : 'Next visit only'}
        onClose={onClose}
      >
        <div className="flex-1 overflow-y-auto px-6 py-10 text-center text-sm text-muted-foreground">
          {configQuery.isLoading
            ? 'Loading…'
            : 'Recovery coupons are disabled. Enable them in Admin → Promo codes.'}
        </div>
        <Footer>
          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Shell>
    )
  }

  return (
    <Shell
      subtitle={displayName ? `For ${displayName} · next visit only` : 'Next visit only'}
      onClose={onClose}
    >
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {/* Tiers */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">Severity</label>
          <div className="grid grid-cols-3 gap-2">
            {config!.tiers.map((t) => {
              const active = t.key === tierKey
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setTierKey(t.key)
                    setOverride('')
                  }}
                  className={`rounded-xl border p-2.5 text-center transition-colors ${
                    active
                      ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500/40 dark:bg-amber-950/40'
                      : 'border-border/70 hover:bg-muted'
                  }`}
                >
                  <div className="text-xs font-semibold text-foreground">{t.label}</div>
                  <div className="text-lg font-bold tabular-nums text-foreground">{t.percent}%</div>
                  <div className="text-[10px] text-muted-foreground">{t.validity_days}d</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Override */}
        <div className="space-y-1.5">
          <label htmlFor="recovery-override" className="text-xs font-medium text-foreground">
            Custom % (optional, max {config!.override_cap}%)
          </label>
          <input
            id="recovery-override"
            type="number"
            inputMode="decimal"
            min={1}
            max={config!.override_cap}
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder={`Tier default: ${selectedTier?.percent ?? 0}%`}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
              overrideInvalid
                ? 'border-red-400 focus:ring-red-200'
                : 'border-border focus:ring-amber-200'
            }`}
          />
          {overrideInvalid && (
            <p className="text-[11px] text-red-500">
              Enter a value between 1 and {config!.override_cap}%.
            </p>
          )}
        </div>

        {/* Reason */}
        <div className="space-y-1.5">
          <label htmlFor="recovery-reason" className="text-xs font-medium text-foreground">
            Reason
          </label>
          <select
            id="recovery-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-input bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            {config!.reasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {/* PIN */}
        <div className="space-y-2 border-t border-border/60 pt-4">
          <label className="block text-center text-xs font-medium text-foreground">
            Manager PIN to authorise {effectivePercent}% off
          </label>
          <div className={`flex justify-center gap-3 ${shake ? 'animate-shake' : ''}`}>
            {pin.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-12 w-12 rounded-xl border-2 border-input bg-background text-foreground text-center text-2xl font-bold focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            ))}
          </div>
          {error && <p className="mt-1 text-center text-sm text-red-500">{error}</p>}
        </div>
      </div>

      <Footer>
        <Button className="w-full" onClick={() => issueMutation.mutate()} disabled={!canSubmit}>
          {issueMutation.isPending ? 'Issuing…' : 'Issue coupon'}
        </Button>
      </Footer>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out 2; }
      `}</style>
    </Shell>
  )
}

/**
 * Dialog shell shared by every state: portals to document.body (escaping the
 * checkout header's backdrop-filter containing block), caps its height, and
 * lays out a sticky amber header above the caller's scrollable body + footer.
 */
function Shell({
  subtitle,
  onClose,
  children,
}: {
  subtitle: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="flex max-h-[min(90vh,640px)] w-full max-w-md flex-col gap-0 overflow-hidden p-0">
        <div className="flex shrink-0 items-center gap-3 border-b border-amber-100 bg-amber-50 px-5 py-4 pr-12 dark:border-amber-900/40 dark:bg-amber-950/40">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300">
            <LifeBuoy className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-base font-bold leading-tight text-amber-900 dark:text-amber-100">
              Recovery coupon
            </DialogTitle>
            <DialogDescription className="truncate text-xs text-amber-700/80 dark:text-amber-200/70">
              {subtitle}
            </DialogDescription>
          </div>
        </div>
        {children}
      </DialogContent>
    </Dialog>
  )
}

/** Sticky action row pinned to the bottom of the modal. */
function Footer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex shrink-0 gap-3 border-t border-border/70 bg-card/60 p-4">{children}</div>
  )
}
